import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Download, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Panel } from "@/components/PageHeader";
import { RichEditor } from "@/components/RichEditor";
import { api, errMsg, fileUrl, fmtDate, todayStr, MAX_FILE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PDF_MAX = 10 * 1024 * 1024;

const checkFile = (e, re, max, msg) => {
  const f = e.target.files?.[0];
  if (f && (!re.test(f.name) || f.size > max)) {
    e.target.value = "";
    toast.error(msg);
    return null;
  }
  return f || null;
};

const NewsForm = ({ editing, onDone }) => {
  const [titolo, setTitolo] = useState("");
  const [testo, setTesto] = useState("");
  const [data, setData] = useState(todayStr());
  const [foto, setFoto] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [key, setKey] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitolo(editing?.titolo || "");
    setTesto(editing?.testo || "");
    setData(editing ? editing.data.slice(0, 10) : todayStr());
    setFoto(null);
    setPdf(null);
    setRemovePdf(false);
    setKey((k) => k + 1);
  }, [editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!testo) return toast.error("Scrivi il testo della news");
    const fd = new FormData();
    fd.append("titolo", titolo);
    fd.append("testo", testo);
    fd.append("data_pubblicazione", data);
    if (foto) fd.append("foto", foto);
    if (pdf) fd.append("allegato", pdf);
    if (editing && removePdf) fd.append("rimuovi_allegato", "true");
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/notizie/${editing.id}`, fd);
      else await api.post("/admin/notizie", fd);
      toast.success(editing ? "News aggiornata" : "News pubblicata");
      if (!editing) { setTitolo(""); setTesto(""); setData(todayStr()); setFoto(null); setPdf(null); setKey((k) => k + 1); }
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="news-form">
      <div className="grid sm:grid-cols-[1fr_200px] gap-4">
        <div><Label>Titolo</Label><Input required value={titolo} onChange={(e) => setTitolo(e.target.value)} data-testid="news-title-input" /></div>
        <div><Label>Data di pubblicazione</Label><Input type="date" required value={data} onChange={(e) => setData(e.target.value)} data-testid="news-date-input" /></div>
      </div>
      <div><Label>Testo</Label><RichEditor value={testo} onChange={setTesto} minHeight={340} testid="news-text-input" /></div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Foto (JPG o PNG, max 2 MB){editing?.foto_id && " – vuoto per mantenere quella attuale"}</Label>
          <Input key={`f${key}`} type="file" accept=".jpg,.jpeg,.png" onChange={(e) => setFoto(checkFile(e, /\.(jpe?g|png)$/i, MAX_FILE, "Foto JPG o PNG, massimo 2 MB"))} data-testid="news-photo-input" />
        </div>
        <div>
          <Label>Allegato PDF (facoltativo, max 10 MB)</Label>
          <Input key={`p${key}`} type="file" accept=".pdf" onChange={(e) => setPdf(checkFile(e, /\.pdf$/i, PDF_MAX, "Allegato PDF, massimo 10 MB"))} data-testid="news-pdf-input" />
          {editing?.allegato && (
            <div className="flex items-center gap-2 mt-2 text-xs">
              <FileText className="h-4 w-4 text-red-600" /><span className="truncate">{editing.allegato_nome}</span>
              <label className="flex items-center gap-1 ml-auto cursor-pointer"><Checkbox checked={removePdf} onCheckedChange={(v) => setRemovePdf(!!v)} data-testid="news-pdf-remove-checkbox" />Rimuovi</label>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="news-submit-button">{saving ? "Salvataggio…" : editing ? "Salva modifiche" : "Pubblica news"}</Button>
        {editing && <Button type="button" variant="outline" onClick={onDone} data-testid="news-cancel-edit-button">Annulla</Button>}
      </div>
    </form>
  );
};

const useImport = (onDone) => {
  const [stato, setStato] = useState(null);
  const timer = useRef(null);
  const poll = async () => {
    const { data } = await api.get("/admin/notizie/importa-sito/stato");
    setStato(data);
    if (data.running) timer.current = setTimeout(poll, 2500);
    else if (data.finito_at) {
      if (data.errore) toast.error(data.errore);
      else toast.success(`Import completato: ${data.importate} nuove, ${data.aggiornate} aggiornate, ${data.gia_presenti} già presenti`);
      onDone();
    }
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const start = async () => {
    try {
      await api.post("/admin/notizie/importa-sito");
    } catch (e) {
      if (e.response?.status !== 409) return toast.error(errMsg(e));
    }
    poll();
  };
  return { running: stato?.running, label: stato?.running ? `Importazione ${stato.fatte}/${stato.totale || "…"}` : "Importa da nacra-9er.it", start };
};

const NewsRow = ({ n, onEdit, onRemove, canEdit }) => (
  <div className="py-3 flex gap-4 items-center" data-testid={`news-admin-row-${n.id}`}>
    <img src={n.foto_id ? fileUrl(n.foto_id) : "/news-default.jpg"} alt="" className="h-14 w-20 object-cover rounded-md bg-slate-100" />
    <div className="flex-1 min-w-0">
      <div className="font-medium truncate">{n.titolo}</div>
      <div className="text-xs text-muted-foreground">{fmtDate(n.data)} · {n.autore}{n.allegato ? " · PDF" : ""}</div>
    </div>
    {canEdit && <>
    <Button size="icon" variant="ghost" onClick={() => onEdit(n)} data-testid={`news-edit-button-${n.id}`}><Pencil className="h-4 w-4" /></Button>
    <Button size="icon" variant="ghost" className="text-red-600" onClick={() => onRemove(n)} data-testid={`news-delete-button-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
    </>}
  </div>
);

export const NewsAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const load = () => api.get("/notizie", { params: { limit: 200 } }).then((r) => setItems(r.data.filter((n) => n.interna)));
  const imp = useImport(load);
  useEffect(() => {
    load();
  }, []);

  const remove = async (n) => {
    if (!window.confirm(`Eliminare la news "${n.titolo}"?`)) return;
    try {
      await api.delete(`/admin/notizie/${n.id}`);
      toast.success("News eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const edit = (n) => {
    setEditing(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-6">
      <Panel>
        <h2 className="font-heading text-2xl uppercase mb-1">{editing ? "Modifica news" : "Nuova news"}</h2>
        <p className="text-sm text-muted-foreground mb-5">Appare nella pagina News, in Home e nella pagina iniziale pubblica.</p>
        <NewsForm editing={editing} onDone={() => { setEditing(null); load(); }} />
      </Panel>
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-heading text-2xl uppercase">News pubblicate ({items.length})</h2>
          {isAdmin && <Button variant="outline" size="sm" onClick={imp.start} disabled={imp.running} data-testid="news-import-site-button">
            <Download className="h-4 w-4 mr-1" />{imp.label}
          </Button>}
        </div>
        {items.length === 0 && <p className="text-muted-foreground">Nessuna news inserita.</p>}
        <div className="divide-y max-h-[640px] overflow-y-auto pr-1">
          {items.map((n) => <NewsRow key={n.id} n={n} onEdit={edit} onRemove={remove} canEdit={isAdmin || n.autore_id === user.id} />)}
        </div>
      </Panel>
    </div>
  );
};
